import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faFileContract,
  faCheck,
  faSpinner,
  faShieldAlt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import apiClient from '../../utils/api';
import { sanitizeEmailHtml } from '../../utils/sanitize';

const TermsModal = ({
  isOpen,
  onClose,
  onAccept,
  version = null,
  showAcceptButton = true,
  title = "Terms and Conditions",
  requireScroll = true
}) => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(!requireScroll);

  useEffect(() => {
    if (isOpen) {
      fetchTerms();
      setHasScrolledToBottom(!requireScroll);
    }
  }, [isOpen, version, requireScroll]);

  const fetchTerms = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = version
        ? `/api/terms/version/${version}`
        : '/api/terms/current';
      const response = await apiClient.get(endpoint);

      if (response.success && response.terms) {
        setTerms(response.terms);
      } else {
        setError('No terms and conditions available');
      }
    } catch (err) {
      console.error('Error fetching terms:', err);
      setError('Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    if (!requireScroll) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider scrolled to bottom if within 50px
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!terms?.version) return;

    setAccepting(true);
    try {
      const response = await apiClient.post('/api/terms/accept', {
        version: terms.version
      });

      if (response.success) {
        if (onAccept) {
          onAccept(terms.version);
        }
      } else {
        setError('Failed to accept terms');
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !showAcceptButton) {
            onClose();
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faFileContract} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {terms?.title || title}
                </h2>
                {terms?.version && (
                  <p className="text-sm text-blue-100">Version {terms.version}</p>
                )}
              </div>
            </div>
            {!showAcceptButton && (
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-900 scrollbar-thin"
            onScroll={handleScroll}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="text-4xl text-blue-500 mb-4"
                />
                <p className="text-gray-500 dark:text-gray-400">Loading terms...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-4xl text-red-500 mb-4"
                />
                <p className="text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={fetchTerms}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : terms ? (
              <div className="max-w-none">
                {/* Summary if available */}
                {terms.summary && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <h3 className="text-blue-800 dark:text-blue-200 font-semibold mb-2 flex items-center gap-2">
                      <FontAwesomeIcon icon={faShieldAlt} />
                      Summary
                    </h3>
                    <p className="text-blue-700 dark:text-blue-300 text-sm m-0">
                      {terms.summary}
                    </p>
                  </div>
                )}

                {/* Main content - rendered with Tailwind typography styles */}
                <div
                  className="
                    text-slate-700 dark:text-slate-300 leading-relaxed

                    [&_.terms-hero]:bg-gradient-to-r [&_.terms-hero]:from-indigo-600 [&_.terms-hero]:to-purple-600
                    [&_.terms-hero]:text-white [&_.terms-hero]:p-6 [&_.terms-hero]:rounded-xl [&_.terms-hero]:mb-8
                    [&_.terms-hero_h1]:text-2xl [&_.terms-hero_h1]:font-bold [&_.terms-hero_h1]:mb-1 [&_.terms-hero_h1]:text-white
                    [&_.terms-hero_p]:text-indigo-100 [&_.terms-hero_p]:m-0 [&_.terms-hero_p]:text-sm

                    [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-800 [&_h2]:dark:text-slate-100
                    [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-slate-200 [&_h2]:dark:border-slate-600

                    [&_h3]:text-base [&_h3]:font-medium [&_h3]:text-slate-700 [&_h3]:dark:text-slate-200
                    [&_h3]:mt-5 [&_h3]:mb-2

                    [&_p]:my-3 [&_p]:text-[15px]

                    [&_ul]:my-4 [&_ul]:pl-5 [&_ul]:space-y-2
                    [&_ol]:my-4 [&_ol]:pl-5 [&_ol]:space-y-2
                    [&_li]:text-[15px] [&_li]:text-slate-600 [&_li]:dark:text-slate-300
                    [&_ul]:list-disc [&_ol]:list-decimal
                    [&_li]:marker:text-indigo-500

                    [&_.feature-list]:bg-slate-50 [&_.feature-list]:dark:bg-slate-800/50
                    [&_.feature-list]:p-4 [&_.feature-list]:pl-8 [&_.feature-list]:rounded-lg
                    [&_.feature-list]:border-l-4 [&_.feature-list]:border-indigo-500

                    [&_.warning-box]:bg-amber-50 [&_.warning-box]:dark:bg-amber-900/20
                    [&_.warning-box]:border [&_.warning-box]:border-amber-200 [&_.warning-box]:dark:border-amber-700
                    [&_.warning-box]:p-4 [&_.warning-box]:rounded-lg [&_.warning-box]:my-4
                    [&_.warning-box]:text-amber-800 [&_.warning-box]:dark:text-amber-200

                    [&_.info-box]:bg-blue-50 [&_.info-box]:dark:bg-blue-900/20
                    [&_.info-box]:border [&_.info-box]:border-blue-200 [&_.info-box]:dark:border-blue-700
                    [&_.info-box]:p-4 [&_.info-box]:rounded-lg [&_.info-box]:my-4
                    [&_.info-box]:text-blue-800 [&_.info-box]:dark:text-blue-200
                    [&_.info-box_p]:m-0

                    [&_.success-box]:bg-emerald-50 [&_.success-box]:dark:bg-emerald-900/20
                    [&_.success-box]:border [&_.success-box]:border-emerald-200 [&_.success-box]:dark:border-emerald-700
                    [&_.success-box]:p-4 [&_.success-box]:rounded-lg [&_.success-box]:my-6
                    [&_.success-box]:text-emerald-800 [&_.success-box]:dark:text-emerald-200
                    [&_.success-box_p]:m-0

                    [&_.contact-box]:bg-slate-100 [&_.contact-box]:dark:bg-slate-700/50
                    [&_.contact-box]:p-4 [&_.contact-box]:rounded-lg [&_.contact-box]:my-4
                    [&_.contact-box_p]:my-1 [&_.contact-box_p]:text-slate-700 [&_.contact-box_p]:dark:text-slate-200

                    [&_a]:text-indigo-600 [&_a]:dark:text-indigo-400 [&_a]:font-medium [&_a]:hover:underline
                    [&_strong]:font-semibold [&_strong]:text-slate-800 [&_strong]:dark:text-slate-100
                  "
                  dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(terms.content) }}
                />

                {/* Effective date */}
                {terms.effective_date && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    Effective Date: {new Date(terms.effective_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer with Accept button */}
          {showAcceptButton && terms && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {requireScroll && !hasScrolledToBottom && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  Please scroll to read the entire document before accepting
                </p>
              )}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={handleAccept}
                  disabled={accepting || (requireScroll && !hasScrolledToBottom)}
                  className={`px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                    accepting || (requireScroll && !hasScrolledToBottom)
                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {accepting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      I Accept the Terms
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsModal;
